/**
 * Iniciales — pantalla unificada de pagos iniciales (enganche).
 *
 * Centro de operaciones de los iniciales: el listado es la vista principal
 * (filtros por cliente/documento, sucursal, tipo facturados/no facturados y
 * rango de fechas, vía /api/reportes_inicialesnocobrados — ares4035; la
 * búsqueda por cliente la resuelve el servidor, así que alcanza a todo el
 * rango de fechas pedido y no solo a la página cargada). Por fila se puede
 * reimprimir el recibo y, en los no facturados, anular el cobro
 * (/api/cancelarinicial — ares4025 con permiso de borrado; baja lógica:
 * la fila queda con status 'A' y se consulta en el tipo "Anulados"). El botón
 * "Cobrar inicial" abre un diálogo con el formulario de cobro
 * (/api/cobroinicial_* — ares4020).
 *
 * Sustituye a las pantallas cobroinicial.js (ares4020) y
 * cancelarinicial.js (ares4025).
 */
import React, { useState, useMemo, useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, PageHeader, StatCard, DocumentoField, BarraVolver, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi, useSucursales } from "lib/api";
import { documentoValido, formatDocumentoAuto, etiquetaDocumento } from "lib/documento";
import { imprimirReciboPdf } from "lib/recibopdf";
import { tienePermisosModulo, puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import useAccionInicial from "lib/useAccionInicial";
import { imprimirListadoGrande } from "lib/impresionhtml";
import CompletivosPendientes from "modules/facturacion/completivospendientes";

// Largo del ticket del recibo de inicial: más largo que el default (355mm) de
// lib/recibopdf, que se queda corto al imprimirlo.
const ALTO_RECIBO_INICIAL = 400;
// La firma baja para que quede espacio de sobra donde firmar (default: 10mm).
const ESPACIO_FIRMA_INICIAL = 40;

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

// Montos digitados con coma de miles mientras se escribe ("1234567.5" -> "1,234,567.5").
const formatMonto = (v) => {
  let s = String(v ?? "").replace(/[^\d.]/g, "");
  const punto = s.indexOf(".");
  if (punto !== -1) s = s.slice(0, punto + 1) + s.slice(punto + 1).replace(/\./g, "");
  const [ent, dec] = s.split(".");
  const entFmt = ent.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return dec !== undefined ? `${entFmt}.${dec}` : entFmt;
};

// El backend espera números sin separador (float() no acepta comas).
const sinComas = (v) => String(v ?? "").replace(/,/g, "");

// Extrae el código numérico de una etiqueta de sucursal "5-LOS MINA" -> "5".
// "0-TODOS" o vacío -> "" (todas).
const codigoSucursal = (label) => {
  const s = String(label || "").split("-")[0].trim();
  return s === "0" || s === "" ? "" : s;
};

// Filtro local del chasis: matchea por chasis, marca, modelo o color y limita
// los resultados renderizados. Soporta opciones string (freeSolo).
const filtrarChasis = createFilterOptions({
  stringify: (o) => (typeof o === "string" ? o : `${o.chasis} ${o.marca || ""} ${o.modelo || ""} ${o.colore || ""}`),
  limit: 60,
});

const FORM_INICIAL = {
  chasis: "", marca: "", modelo: "", colore: "",
  cedula: "", nombres: "", apellidos: "", valor: "",
  // Formas de pago del cobro. Con una sola, su monto es el Valor completo;
  // con varias (combinado) cada una lleva su monto y deben sumar el Valor.
  pagos: [{ tipopago: "Contado", valor: "", referencia: "" }],
};

// Desglose del recibo por forma de pago (cobro combinado): una línea por
// forma, con su referencia, encima del total.
const totalesRecibo = (formas, valor) => [
  ...(Array.isArray(formas) && formas.length > 1
    ? formas.map((f) => ({
        etiqueta: f.referencia ? `${f.tipopago} (${f.referencia})` : f.tipopago,
        valor: f.valor,
      }))
    : []),
  { etiqueta: "Valor", valor, destacado: true },
];

// Formas de un recibo: las del grupo (combinado) o la fila sola.
const formasDe = (row) =>
  Array.isArray(row.formas) && row.formas.length > 1 ? row.formas : null;

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function NoRowsOverlay() {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", py: 6, color: FP.color.textMuted }}>
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>Sin iniciales en el rango</Typography>
    </Stack>
  );
}

// El backend serializa las columnas DATE como medianoche GMT ("Tue, 30 Sep
// 2026 00:00:00 GMT"); leídas en hora local de RD (UTC-4) caían un día antes.
// Se formatean en UTC para mostrar el día tal como está guardado.
function fmtFechaDia(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString("es-DO", { timeZone: "UTC" });
}

// Imprime el listado agregado de iniciales (mismo patrón de iframe que
// Delegado al formato compartido estilo factura x mayor (lib/impresionhtml).
function imprimirListado(empresa, titulo, subtitulo, cabeceras, filas, totales) {
  imprimirListadoGrande({ compania: empresa, titulo, subtitulo, cabeceras, filas, totales });
}

// Reimprime el recibo de un inicial del listado (y desde el expediente del
// financiamiento), en PDF con el formato de
// bankivo (lib/recibopdf).
// Día del inicial más la hora grabada del cobro (inicial.hora, "HH:MM:SS"
// en hora de RD), con el mismo formato que el recibo de pagos. Los iniciales
// anteriores a la columna, o cobrados en el escritorio, no tienen hora.
export function fechaHoraInicial(fecha, hora) {
  const dia = fmtFechaDia(fecha);
  const m = String(hora || "").match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return dia;
  const h = new Date(2000, 0, 1, Number(m[1]), Number(m[2]), Number(m[3] || 0));
  return `${dia} ${h.toLocaleTimeString("es-DO")}`;
}

export function imprimirReciboInicial(row, compania) {
  const fechaStr = fechaHoraInicial(row.fecha, row.hora);
  imprimirReciboPdf({
    titulo: "Recibo de Cobro Inicial",
    compania,
    campos: [
      row.id ? ["Recibo #", row.id] : null,
      ["Fecha", fechaStr],
      ["Cliente", row.nombres],
      [etiquetaDocumento(row.cedula), row.cedula],
      ["Chasis", row.chasis],
      ["Vehiculo", [row.marca, row.modelo, row.colore].filter(Boolean).join(" ")],
      formasDe(row) || row.banco ? ["Tipo de pago", formasDe(row) ? "Combinado" : row.banco] : null,
      !formasDe(row) && row.referencia ? ["Referencia", row.referencia] : null,
    ],
    totales: totalesRecibo(formasDe(row), row.valor),
    firma: "Firma",
    alto: ALTO_RECIBO_INICIAL,
    espacioFirma: ESPACIO_FIRMA_INICIAL,
  });
}

// Fecha y hora del momento del cobro, igual que el recibo de pagos
// (cobropagos): el inicial solo guarda el día, la hora no queda grabada.
const fechaHoraAhora = () => {
  const now = new Date();
  return `${now.toLocaleDateString("es-DO")} ${now.toLocaleTimeString("es-DO")}`;
};

// Recibo de un cobro recién guardado, mismo formato PDF.
function imprimirReciboNuevo(recibo, compania) {
  imprimirReciboPdf({
    titulo: "Recibo de Cobro Inicial",
    compania,
    campos: [
      recibo.id ? ["Recibo #", recibo.id] : null,
      ["Fecha", fechaHoraAhora()],
      [etiquetaDocumento(recibo.cedula), recibo.cedula],
      ["Cliente", `${recibo.nombres} ${recibo.apellidos}`],
      ["Chasis", recibo.chasis],
      ["Vehiculo", [recibo.marca, recibo.modelo, recibo.colore].filter(Boolean).join(" ")],
      ["Tipo de pago", formasDe(recibo) ? "Combinado" : recibo.tipopago],
      !formasDe(recibo) && recibo.descripcionpago ? ["Descripcion", recibo.descripcionpago] : null,
    ],
    totales: totalesRecibo(formasDe(recibo), recibo.valor),
    firma: "Firma",
    alto: ALTO_RECIBO_INICIAL,
    espacioFirma: ESPACIO_FIRMA_INICIAL,
  });
}

/**
 * Diálogo de cobro de inicial (ares4020). Carga los chasis elegibles al
 * abrirse; al guardar imprime el recibo, limpia el formulario (para cobrar
 * varios seguidos) y notifica al listado vía onSaved(data).
 *
 * También se abre desde la venta a crédito: `prefill` ({ cedula, tipodoc,
 * nombres, apellidos, chasis, valor }) llega con el cliente y el chasis de la
 * venta, y `cerrarAlGuardar` lo cierra tras el cobro. Sin `tipospago` ni
 * `compania` (fuera de la pantalla de Iniciales) carga /api/cobroinicial_config.
 */
export function CobroInicialDialog({
  open,
  onClose,
  onSaved,
  compania: companiaProp,
  tipospago: tipospagoProp,
  prefill,
  cerrarAlGuardar,
}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [configPropia, setConfigPropia] = useState(null);
  useEffect(() => {
    if (!open || tipospagoProp || configPropia) return undefined;
    let activo = true;
    post("/api/cobroinicial_config", {}).then(({ ok, data }) => {
      if (activo && ok) setConfigPropia(data);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const tipospago =
    tipospagoProp ||
    (Array.isArray(configPropia?.tipospago) && configPropia.tipospago.length
      ? configPropia.tipospago
      : ["Contado", "Transferencias", "Tarjeta"]);
  const compania = companiaProp || configPropia?.compania || "";

  const [form, setForm] = useState(FORM_INICIAL);
  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // ---- Formas de pago (cobro combinado) ----
  const combinado = form.pagos.length > 1;
  const faltaAsignar = combinado
    ? toNum(form.valor) - form.pagos.reduce((s, x) => s + toNum(x.valor), 0)
    : 0;
  const setPago = (i, k, v) =>
    setForm((p) => ({ ...p, pagos: p.pagos.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  // Agregar una forma: la primera toma el Valor completo (si aún no tenía
  // monto) y la nueva lo que falte por asignar.
  const agregarPago = () =>
    setForm((p) => {
      const pagos = p.pagos.map((x, j) =>
        j === 0 && p.pagos.length === 1 ? { ...x, valor: p.valor } : x
      );
      const falta = toNum(p.valor) - pagos.reduce((s, x) => s + toNum(x.valor), 0);
      const usados = new Set(pagos.map((x) => x.tipopago));
      const tipo = tipospago.find((t) => !usados.has(t)) || tipospago[0];
      return {
        ...p,
        pagos: [
          ...pagos,
          { tipopago: tipo, valor: falta > 0 ? formatMonto(falta.toFixed(2)) : "", referencia: "" },
        ],
      };
    });
  let textoAsignado = "Las formas de pago cubren el valor";
  if (faltaAsignar > 0.009) textoAsignado = `Falta asignar RD$ ${formatMonto(faltaAsignar.toFixed(2))}`;
  if (faltaAsignar < -0.009) textoAsignado = `Se pasa por RD$ ${formatMonto((-faltaAsignar).toFixed(2))}`;
  const quitarPago = (i) =>
    setForm((p) => ({ ...p, pagos: p.pagos.filter((_, j) => j !== i) }));

  // Tipo de documento del cliente (cedula/rnc/pasaporte): solo controla la
  // máscara y validación; en la base va en la misma columna cedula.
  const [tipodoc, setTipodoc] = useState("cedula");

  const [chasisOpts, setChasisOpts] = useState([]);
  const [loadingChasis, setLoadingChasis] = useState(false);
  const [chasisValidado, setChasisValidado] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cargarChasis = useCallback(async () => {
    setLoadingChasis(true);
    try {
      const { ok, data } = await post("/api/cobroinicial_chasis", {});
      if (ok && Array.isArray(data.chasis)) setChasisOpts(data.chasis);
    } finally {
      setLoadingChasis(false);
    }
  }, [post]);

  const validarChasis = useCallback(
    async (chasis) => {
      if (!chasis) return;
      const { ok, data } = await post("/api/cobroinicial_validarchasis", { chasis });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo validar el chasis", color: "error" });
        setChasisValidado(false);
        setForm((p) => ({ ...p, chasis: "", marca: "", modelo: "", colore: "" }));
        return;
      }
      setChasisValidado(true);
      setForm((p) => ({
        ...p,
        chasis: data.chasis,
        marca: data.marca,
        modelo: data.modelo,
        colore: data.colore,
      }));
    },
    [post, showAlert]
  );

  // Al abrir: formulario limpio, o con el cliente y el chasis de la venta.
  useEffect(() => {
    if (open) {
      setForm(
        prefill
          ? {
              ...FORM_INICIAL,
              cedula: prefill.cedula || "",
              nombres: prefill.nombres || "",
              apellidos: prefill.apellidos || "",
              valor: toNum(prefill.valor) > 0 ? formatMonto(String(prefill.valor)) : "",
            }
          : FORM_INICIAL
      );
      setTipodoc(prefill?.tipodoc || "cedula");
      setChasisValidado(false);
      setSolicitud(null);
      cargarChasis();
      if (prefill?.chasis) validarChasis(prefill.chasis);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---- Solicitud de crédito vinculada (autollenado por cédula) ----
  // Con una solicitud vigente para la cédula se hala todo lo disponible:
  // cliente, chasis (validado) y el restante del inicial pactado como valor
  // sugerido. Sin solicitud, el diálogo funciona como siempre.
  const [solicitud, setSolicitud] = useState(null);
  const [solOpts, setSolOpts] = useState([]); // varias solicitudes -> selección
  const [solOpen, setSolOpen] = useState(false);

  const aplicarSolicitud = useCallback(
    (s) => {
      setSolicitud(s);
      setForm((p) => ({
        ...p,
        nombres: s.nombres || p.nombres,
        apellidos: s.apellidos || p.apellidos,
        valor: p.valor || (toNum(s.restante) > 0 ? formatMonto(String(s.restante)) : ""),
      }));
      if (s.chasis) validarChasis(s.chasis);
    },
    [validarChasis]
  );

  const buscarSolicitudPorCedula = useCallback(async () => {
    const ced = form.cedula.trim();
    if (!documentoValido(ced, tipodoc)) return;
    const { ok, data } = await post("/api/solicitudcredito_porcedula", { cedula: ced });
    if (!ok || !Array.isArray(data.solicitudes) || data.solicitudes.length === 0) {
      setSolicitud(null);
      return;
    }
    if (data.solicitudes.length === 1) {
      aplicarSolicitud(data.solicitudes[0]);
      return;
    }
    setSolOpts(data.solicitudes);
    setSolOpen(true);
  }, [form.cedula, tipodoc, post, aplicarSolicitud]);

  const limpiar = () => {
    setForm(FORM_INICIAL);
    setTipodoc("cedula");
    setChasisValidado(false);
    setSolicitud(null);
  };

  const guardar = async () => {
    if (!chasisValidado || !form.chasis) {
      showAlert({ message: "Seleccione un chasis válido de la lista", color: "warning" });
      return;
    }
    if (!documentoValido(form.cedula, tipodoc)) {
      showAlert({ message: "El documento del cliente está incompleto", color: "warning" });
      return;
    }
    if (!form.nombres.trim() || !form.apellidos.trim()) {
      showAlert({ message: "Indique nombres y apellidos", color: "warning" });
      return;
    }
    if (toNum(form.valor) <= 0) {
      showAlert({ message: "El valor no puede ser 0", color: "warning" });
      return;
    }
    if (combinado) {
      if (form.pagos.some((x) => toNum(x.valor) <= 0)) {
        showAlert({ message: "Cada forma de pago debe tener un monto mayor que 0", color: "warning" });
        return;
      }
      if (Math.abs(faltaAsignar) > 0.009) {
        showAlert({
          message: `Las formas de pago deben sumar RD$ ${formatMonto(sinComas(form.valor))}`,
          color: "warning",
        });
        return;
      }
    }
    setGuardando(true);
    try {
      // El valor viaja sin comas: el backend lo parsea con float(). Una sola
      // forma va con el contrato de siempre (tipopago + descripcionpago).
      const { pagos, ...resto } = form;
      const { ok, data } = await post("/api/cobroinicial_guardar", {
        ...resto,
        valor: sinComas(form.valor),
        ...(combinado
          ? { pagos: pagos.map((x) => ({ ...x, valor: sinComas(x.valor) })) }
          : { tipopago: pagos[0].tipopago, descripcionpago: pagos[0].referencia }),
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Cobro inicial registrado", color: "success" });
        // Multi-empresa: el backend devuelve el membrete de la empresa dueña del chasis.
        if (data.recibo) imprimirReciboNuevo(data.recibo, data.compania || compania);
        limpiar();
        if (cerrarAlGuardar) onClose();
        else cargarChasis();
        onSaved(data);
      } else {
        showAlert({ message: data.Error || "Error al guardar", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardando(false);
    }
  };

  const hoy = new Date().toLocaleDateString("es-DO", { day: "2-digit", month: "2-digit", year: "numeric" });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
        <PaymentsOutlinedIcon sx={{ color: FP.color.navy }} />
        Cobrar inicial
        <Box sx={{ flex: 1 }} />
        <Chip label={`Fecha: ${hoy}`} sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }} />
      </DialogTitle>
      <DialogContent>
        {/* La cédula va de primero: con una solicitud de crédito vigente se
            autollena cliente + chasis y el valor sugerido del inicial. */}
        <Box sx={{ ...sectionBox, mt: 1 }}>
          <SectionTitle icon={PersonOutlineIcon} color={FP.color.info}>Cliente</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <DocumentoField
                autoFocus
                value={form.cedula}
                tipo={tipodoc}
                onChangeValor={(v) => setForm((p) => ({ ...p, cedula: v }))}
                onChangeTipo={setTipodoc}
                onBlur={buscarSolicitudPorCedula}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Nombres" value={form.nombres} onChange={setF("nombres")} sx={fpInputSx} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Apellidos" value={form.apellidos} onChange={setF("apellidos")} sx={fpInputSx} />
            </Grid>
          </Grid>
          {solicitud ? (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Chip
                label={`Solicitud #${solicitud.nosolic}`}
                sx={{ bgcolor: FP.color.accentBlueSoft, color: FP.color.accentBlue, fontWeight: 700 }}
              />
              <Chip
                label={`Pactado: RD$ ${fmtMoney(solicitud.inicial)}`}
                sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }}
              />
              <Chip
                label={`Abonado: RD$ ${fmtMoney(solicitud.abonado)}`}
                sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }}
              />
              <Chip
                label={`Restante: RD$ ${fmtMoney(solicitud.restante)}`}
                sx={{
                  bgcolor: toNum(form.valor) > toNum(solicitud.restante) ? "#FEF3C7" : FP.color.surfaceAlt,
                  color: toNum(form.valor) > toNum(solicitud.restante) ? "#B45309" : FP.color.textSecondary,
                  fontWeight: 700,
                }}
              />
            </Stack>
          ) : null}
        </Box>

        <Box sx={sectionBox}>
          <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.accentBlue}>Producto / Vehículo</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={6}>
              <Autocomplete
                fullWidth
                freeSolo
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarChasis}
                options={chasisOpts}
                loading={loadingChasis}
                value={form.chasis || null}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.chasis || "")}
                isOptionEqualToValue={(o, v) => (o.chasis || o) === (v.chasis || v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.chasis}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>{o.chasis}</Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.marca, o.modelo, o.colore].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                onChange={(_, v) => {
                  const ch = typeof v === "string" ? v : v?.chasis || "";
                  if (ch) validarChasis(ch);
                  else {
                    setChasisValidado(false);
                    setForm((p) => ({ ...p, chasis: "", marca: "", modelo: "", colore: "" }));
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Chasis" sx={fpInputSx} />}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField fullWidth label="Marca" value={form.marca} InputProps={{ readOnly: true }} sx={fpInputSx} />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField fullWidth label="Modelo" value={form.modelo} InputProps={{ readOnly: true }} sx={fpInputSx} />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField fullWidth label="Color" value={form.colore} InputProps={{ readOnly: true }} sx={fpInputSx} />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>Pago</SectionTitle>
          {/* Valor total del cobro. Con una sola forma de pago va en la misma
              fila; combinado, cada forma va en su propia fila debajo, con su
              monto, y el pie muestra cuánto falta por asignar. */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Valor" value={form.valor}
                onChange={(e) => setForm((p) => ({ ...p, valor: formatMonto(e.target.value) }))}
                sx={fpInputSx} inputProps={{ inputMode: "decimal" }}
                InputProps={{ startAdornment: <InputAdornment position="start">RD$</InputAdornment> }} />
            </Grid>
            {!combinado && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField select fullWidth label="Tipo de pago" value={form.pagos[0].tipopago}
                    onChange={(e) => setPago(0, "tipopago", e.target.value)} sx={fpInputSx}>
                    {tipospago.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Descripción / Referencia" value={form.pagos[0].referencia}
                    onChange={(e) => setPago(0, "referencia", e.target.value)} sx={fpInputSx} />
                </Grid>
                <Grid item xs={12}>
                  <Button size="small" startIcon={<AddIcon />} onClick={agregarPago} sx={btnGhost}>
                    Combinar formas de pago
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
          {combinado && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: `1px solid ${FP.color.border}`, bgcolor: "#fff" }}>
              <Typography sx={{ fontSize: "0.74rem", fontWeight: 800, letterSpacing: "0.08em",
                textTransform: "uppercase", color: FP.color.textSecondary, mb: 1.6 }}>
                Formas de pago
              </Typography>
              <Stack spacing={1.6}>
                {form.pagos.map((x, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid container spacing={1.5} key={i} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth label={`Forma de pago ${i + 1}`} value={x.tipopago}
                        onChange={(e) => setPago(i, "tipopago", e.target.value)} sx={fpInputSx}>
                        {tipospago.map((t) => (
                          <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField fullWidth label="Monto" value={x.valor}
                        onChange={(e) => setPago(i, "valor", formatMonto(e.target.value))}
                        sx={fpInputSx} inputProps={{ inputMode: "decimal" }}
                        InputProps={{ startAdornment: <InputAdornment position="start">RD$</InputAdornment> }} />
                    </Grid>
                    <Grid item xs={10} sm={4}>
                      <TextField fullWidth label="Referencia" value={x.referencia}
                        onChange={(e) => setPago(i, "referencia", e.target.value)} sx={fpInputSx} />
                    </Grid>
                    <Grid item xs={2} sm={1} sx={{ display: "flex", justifyContent: "center" }}>
                      <Tooltip title="Quitar esta forma de pago">
                        <IconButton onClick={() => quitarPago(i)} sx={{ color: FP.color.danger }}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                ))}
              </Stack>
              <Box sx={{ mt: 1.6, pt: 1.6, borderTop: `1px solid ${FP.color.border}`, display: "flex",
                alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                <Button size="small" startIcon={<AddIcon />} onClick={agregarPago} sx={btnOutline(FP.color.navy)}>
                  Agregar forma de pago
                </Button>
                <Typography sx={{ fontSize: "0.86rem", fontWeight: 700,
                  color: Math.abs(faltaAsignar) > 0.009 ? FP.color.danger : FP.color.success }}>
                  {textoAsignado}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnGhost}>Limpiar</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={btnGhost}>Cerrar</Button>
        <Button
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={guardar}
          disabled={guardando}
          sx={btnSolid(FP.color.navy, FP.color.navySoft)}
        >
          Guardar
        </Button>
      </DialogActions>

      {/* Selección cuando la cédula tiene varias solicitudes vigentes */}
      <Dialog open={solOpen} onClose={() => setSolOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <PersonOutlineIcon sx={{ color: FP.color.navy }} />
          Solicitudes de crédito del cliente
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary, mb: 2 }}>
            La cédula tiene más de una solicitud vigente. Selecciona a cuál corresponde este cobro.
          </Typography>
          <Stack spacing={1.2}>
            {solOpts.map((s) => (
              <Button
                key={s.id}
                onClick={() => {
                  aplicarSolicitud(s);
                  setSolOpen(false);
                }}
                sx={{ ...btnOutline(FP.color.navy), justifyContent: "space-between", px: 2, py: 1.2 }}
              >
                <span style={{ fontWeight: 800 }}>#{s.nosolic}</span>
                <span>{s.chasis || "Sin chasis"}</span>
                <span>Restante: RD$ {fmtMoney(s.restante)}</span>
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setSolOpen(false)} sx={btnGhost}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

// Fechas locales: toISOString() es UTC y después de las 8 p. m. en RD ya da mañana.
const isoLocal = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const hoyISO = () => isoLocal(new Date());
const inicioMesISO = () => {
  const d = new Date();
  return isoLocal(new Date(d.getFullYear(), d.getMonth(), 1));
};

export default function Iniciales() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const sucursales = useSucursales({ conTodos: true });
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos: cobrar requiere el programa ares4020; anular requiere el
  // programa ares4025 con la acción de borrado (el backend lo re-valida).
  const puedeCobrar = tienePermisosModulo(roleDetalles, "ares4020");
  const puedeAnular = puedeAccionPrograma(roleDetalles, "ares4025", "borrar");

  // Config compartida: compañía para el encabezado de los recibos y tipos de pago.
  // Con multi-empresa, `companias` trae el membrete de cada empresa para que
  // las reimpresiones usen el de la empresa de cada inicial.
  const [compania, setCompania] = useState("");
  const [companias, setCompanias] = useState({});
  const [tipospago, setTipospago] = useState(["Contado", "Transferencias", "Tarjeta"]);
  // Metodología de completivos al inicial (varinicial.completivoinicial):
  // apagada, el botón/reporte de completivos no se muestra.
  const [completivoActivo, setCompletivoActivo] = useState(false);
  useEffect(() => {
    let activo = true;
    post("/api/cobroinicial_config", {}).then(({ ok, data }) => {
      if (!activo || !ok) return;
      if (Array.isArray(data.tipospago) && data.tipospago.length) setTipospago(data.tipospago);
      setCompania(data.compania || "");
      setCompanias(data.companias || {});
      setCompletivoActivo(String(data.completivoinicial || "N").toUpperCase() === "S");
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Filtros ----
  const [sucursal, setSucursal] = useState("0-TODOS");
  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [tipo, setTipo] = useState("nofacturados");
  // Búsqueda por cliente: nombre o documento, parcial y resuelta en el
  // servidor (el listado solo trae el rango de fechas pedido).
  const [busquedaCliente, setBusquedaCliente] = useState("");
  // Multi-empresa: filtro y columna (activo cuando config trae companias)
  const [empresaFiltro, setEmpresaFiltro] = useState("0");
  const multiActivo = Object.keys(companias).length > 0;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Tipo con el que se cargó el listado actual: decide si la fila muestra Anular.
  const [tipoCargado, setTipoCargado] = useState("nofacturados");

  const cargar = useCallback(
    async (ov = {}) => {
      setLoading(true);
      try {
        const tipoConsulta = ov.tipo ?? tipo;
        const { ok, data } = await post("/api/reportes_inicialesnocobrados", {
          sucursal: codigoSucursal(ov.sucursal ?? sucursal),
          fechadesde: ov.fecha1 ?? fecha1,
          fechahasta: ov.fecha2 ?? fecha2,
          tipo: tipoConsulta,
          cliente: (ov.cliente ?? busquedaCliente).trim(),
          ...(multiActivo ? { empresa: Number(ov.empresa ?? empresaFiltro) } : {}),
        });
        if (ok && Array.isArray(data.data)) {
          setRows(data.data.map((r, i) => ({ id: r.id ?? i, ...r })));
          setTipoCargado(tipoConsulta);
        } else {
          showAlert({ message: data.Error || "No se pudo cargar el listado", color: "error" });
        }
      } catch (e) {
        showAlert({ message: "Error de red o servidor", color: "error" });
      } finally {
        setLoading(false);
      }
    },
    [post, sucursal, fecha1, fecha2, tipo, busquedaCliente, showAlert, multiActivo, empresaFiltro]
  );

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpiar = () => {
    setSucursal("0-TODOS");
    setFecha1(inicioMesISO());
    setFecha2(hoyISO());
    setTipo("nofacturados");
    setBusquedaCliente("");
    cargar({
      sucursal: "0-TODOS",
      fecha1: inicioMesISO(),
      fecha2: hoyISO(),
      tipo: "nofacturados",
      cliente: "",
    });
  };

  const kpis = useMemo(() => {
    const total = rows.reduce((s, r) => s + (typeof r.valor === "number" ? r.valor : parseFloat(String(r.valor || "").replace(/,/g, "")) || 0), 0);
    return { count: rows.length, total };
  }, [rows]);

  // ---- Cobrar (diálogo) ----
  const [cobroOpen, setCobroOpen] = useState(false);

  // ---- Completivos del inicial pendientes (ventas financiadas con inicial
  // pactado mayor que lo abonado; se cobran desde la pantalla de pagos), a
  // pantalla completa con sus filtros (ver completivospendientes.js).
  const [compOpen, setCompOpen] = useState(false);
  const abrirCompletivos = () => setCompOpen(true);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    cobrar: { permitido: puedeCobrar, abrir: () => setCobroOpen(true) },
  });

  // ---- Anular ----
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [cancelando, setCancelando] = useState(false);

  // Cobro combinado: las filas del mismo recibo (inicial.grupo) en el listado.
  // Comparten fecha y estado, así que el rango cargado las trae todas.
  const filasDelRecibo = (row) =>
    row.grupo ? rows.filter((r) => String(r.grupo) === String(row.grupo)).sort((a, b) => a.id - b.id) : [row];
  const reciboDeFila = (row) => {
    const filas = filasDelRecibo(row);
    if (filas.length < 2) return row;
    return {
      ...filas[0],
      formas: filas.map((r) => ({ tipopago: r.banco, valor: toNum(r.valor), referencia: r.referencia })),
      valor: filas.reduce((s, r) => s + toNum(r.valor), 0),
    };
  };

  const pedirCancelar = (row) => {
    setSel(row);
    setConfirmOpen(true);
  };
  const confirmarCancelar = async () => {
    if (!sel) return;
    setCancelando(true);
    try {
      const { ok, data } = await post("/api/cancelarinicial", { id: sel.id });
      if (ok) {
        showAlert({ message: data.ok || "Inicial cancelado", color: "success" });
        setConfirmOpen(false);
        setSel(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo cancelar", color: "error" });
      }
    } catch (e) {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setCancelando(false);
    }
  };

  // Anular solo aplica a iniciales no facturados (el facturado ya tiene préstamo).
  const mostrarAnular = puedeAnular && tipoCargado === "nofacturados";
  // Anulados: sin reimpresión (el recibo ya no vale) y con quién/cuándo anuló.
  const verAnulados = tipoCargado === "anulados";
  const fmtFecha = (value) => fmtFechaDia(value) || "—";

  const columns = useMemo(
    () => [
      { field: "fecha", headerName: "Fecha", width: 120, valueFormatter: (value) => fmtFecha(value) },
      { field: "cedula", headerName: "Documento", width: 150, valueFormatter: (value) => formatDocumentoAuto(value) },
      { field: "nombres", headerName: "Cliente", flex: 1, minWidth: 200 },
      { field: "chasis", headerName: "Chasis", width: 180 },
      { field: "sucdescrip", headerName: "Sucursal", width: 150 },
      // Multi-empresa: empresa del inicial (solo con el modo activo)
      ...(multiActivo
        ? [
            {
              field: "empresa",
              headerName: "Empresa",
              width: 130,
              valueGetter: (value, row) =>
                companias[String(row.empresa || 1)]?.nombre || `Empresa ${row.empresa || 1}`,
            },
          ]
        : []),
      {
        field: "valor",
        headerName: "Valor",
        width: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => "RD$ " + fmtMoney(value),
      },
      // Forma de pago de la fila; las de un cobro combinado comparten recibo.
      {
        field: "banco",
        headerName: "Pago",
        width: 150,
        valueGetter: (value, row) => (row.grupo ? `${value || ""} · recibo #${row.grupo}` : value || ""),
      },
      ...(verAnulados
        ? [
            { field: "fechaanulacion", headerName: "Anulado el", width: 120, valueFormatter: (value) => fmtFecha(value) },
            { field: "anuladopor", headerName: "Anulado por", width: 150, valueFormatter: (value) => value || "—" },
          ]
        : [
            {
              field: "acciones",
              headerName: "Acciones",
              width: mostrarAnular ? 230 : 140,
              sortable: false,
              filterable: false,
              renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<PrintOutlinedIcon />}
                    sx={btnOutline(FP.color.navy)}
                    onClick={() =>
                      imprimirReciboInicial(reciboDeFila(params.row), companias[String(params.row.empresa || 1)] || compania)
                    }
                  >
                    Reimprimir
                  </Button>
                  {mostrarAnular && (
                    <Button size="small" startIcon={<HighlightOffIcon />} sx={btnOutline(FP.color.danger)} onClick={() => pedirCancelar(params.row)}>
                      Anular
                    </Button>
                  )}
                </Stack>
              ),
            },
          ]),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [compania, companias, mostrarAnular, verAnulados, rows]
  );

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Iniciales"
        subtitle="Cobra, consulta y anula pagos iniciales (enganche)"
        cta={
          <Stack direction="row" spacing={1.2}>
            {completivoActivo && (
              <Button startIcon={<PaidOutlinedIcon />} onClick={abrirCompletivos} sx={btnOutline(FP.color.navy)}>
                Completivos
              </Button>
            )}
            {puedeCobrar && (
              <Button startIcon={<PaymentsOutlinedIcon />} onClick={() => setCobroOpen(true)} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
                Cobrar inicial
              </Button>
            )}
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* KPIs */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatCard label="Iniciales" value={kpis.count} icon={PaidOutlinedIcon} accent={FP.color.navy} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Monto total" value={`RD$ ${fmtMoney(kpis.total)}`} icon={PaidOutlinedIcon} accent={FP.color.accentBlue} />
          </Grid>
        </Grid>

        {/* Filtros */}
        <Card elevation={0} sx={{ mb: 3, borderRadius: 2.5, border: `1px solid ${FP.color.border}`, boxShadow: FP.shadow.soft }}>
          <CardContent sx={{ p: { xs: 2, md: 2.6 } }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <FilterAltOutlinedIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
              <Typography sx={{ fontWeight: 800, fontSize: "0.74rem", letterSpacing: "0.1em", textTransform: "uppercase", color: FP.color.textSecondary }}>
                Filtros
              </Typography>
            </Stack>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Cliente o documento"
                  placeholder="Nombre, apellido, cédula o RNC"
                  value={busquedaCliente}
                  onChange={(e) => setBusquedaCliente(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") cargar();
                  }}
                  sx={fpInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                      </InputAdornment>
                    ),
                    endAdornment: busquedaCliente ? (
                      <InputAdornment position="end">
                        <Tooltip title="Quitar la búsqueda">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setBusquedaCliente("");
                              cargar({ cliente: "" });
                            }}
                          >
                            <HighlightOffIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.2}>
                <TextField select fullWidth label="Sucursal" value={sucursal} onChange={(e) => setSucursal(e.target.value)} sx={fpInputSx}>
                  {sucursales.map((s) => (
                    <MenuItem key={s.label} value={s.label}>{s.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={1.8}>
                <TextField select fullWidth label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value)} sx={fpInputSx}>
                  <MenuItem value="nofacturados">No facturados</MenuItem>
                  <MenuItem value="facturados">Facturados</MenuItem>
                  <MenuItem value="anulados">Anulados</MenuItem>
                </TextField>
              </Grid>
              {multiActivo && (
                <Grid item xs={12} sm={6} md={2}>
                  <TextField select fullWidth label="Empresa" value={empresaFiltro} onChange={(e) => setEmpresaFiltro(e.target.value)} sx={fpInputSx}>
                    <MenuItem value="0">Todas</MenuItem>
                    {Object.keys(companias).sort().map((n) => (
                      <MenuItem key={n} value={n}>
                        {n} — {companias[n]?.nombre || `Empresa ${n}`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={1.7}>
                <TextField fullWidth type="date" label="Desde" InputLabelProps={{ shrink: true }} value={fecha1} onChange={(e) => setFecha1(e.target.value)} sx={fpInputSx} />
              </Grid>
              <Grid item xs={12} sm={6} md={1.7}>
                <TextField fullWidth type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={fecha2} onChange={(e) => setFecha2(e.target.value)} sx={fpInputSx} />
              </Grid>
              <Grid item xs={12} md={1.6}>
                <Stack direction="row" spacing={1}>
                  <Button fullWidth startIcon={<SearchIcon />} onClick={() => cargar()} disabled={loading} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
                    Buscar
                  </Button>
                  <Tooltip title="Limpiar filtros">
                    <Button onClick={limpiar} sx={{ ...btnOutline(FP.color.navy), minWidth: 44, px: 0 }}>
                      <RestartAltIcon />
                    </Button>
                  </Tooltip>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabla */}
        <Card elevation={0} sx={{ borderRadius: 2.5, border: `1px solid ${FP.color.border}`, boxShadow: FP.shadow.soft, overflow: "hidden" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.8, borderBottom: `1px solid ${FP.color.border}` }}>
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>Iniciales registrados</Typography>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Button
                size="small"
                startIcon={<PrintOutlinedIcon />}
                disabled={!rows.length}
                sx={btnOutline(FP.color.navy)}
                onClick={() =>
                  imprimirListado(
                    compania,
                    `Iniciales ${
                      { facturados: "facturados", anulados: "anulados" }[tipoCargado] || "no facturados"
                    }`,
                    `Del ${fecha1} al ${fecha2}${codigoSucursal(sucursal) ? ` · Sucursal ${sucursal}` : ""}${
                      busquedaCliente.trim() ? ` · Cliente: ${busquedaCliente.trim()}` : ""
                    }`,
                    ["Fecha", "Cédula", "Cliente", "Chasis", "Sucursal", "Valor"],
                    rows.map((r) => {
                      return [fmtFechaDia(r.fecha), r.cedula || "", r.nombres || "", r.chasis || "", r.sucdescrip || "", "RD$ " + fmtMoney(r.valor)];
                    }),
                    ["", "", "", "", "TOTAL", "RD$ " + fmtMoney(kpis.total)]
                  )
                }
              >
                Imprimir listado
              </Button>
              <Chip size="small" label={`${rows.length} ${rows.length === 1 ? "registro" : "registros"}`} sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }} />
            </Stack>
          </Stack>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={52}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "260px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Card>
      </Box>

      {/* Cobrar inicial */}
      <CobroInicialDialog
        open={cobroOpen}
        onClose={() => setCobroOpen(false)}
        onSaved={() => cargar()}
        compania={compania}
        tipospago={tipospago}
      />

      {/* Completivos del inicial pendientes (pantalla completa) */}
      <Dialog fullScreen open={compOpen} onClose={() => setCompOpen(false)}>
        <BarraVolver onExit={() => setCompOpen(false)} />
        {compOpen && <CompletivosPendientes compania={compania} />}
      </Dialog>

      {/* Confirmar anulación */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <HighlightOffIcon sx={{ color: FP.color.danger }} />
          Anular cobro inicial
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary }}>
            ¿Deseas anular este cobro inicial? Quedará marcado como anulado (visible en el tipo
            &quot;Anulados&quot;), dejará de contar en la venta y en el cuadre, y el producto volverá a estar disponible.
          </Typography>
          {sel && (
            <Box sx={{ mt: 2, p: 1.6, borderRadius: 2, bgcolor: FP.color.surfaceAlt }}>
              <Typography sx={{ fontWeight: 700 }}>{sel.nombres}</Typography>
              <Typography sx={{ fontSize: "0.8rem", color: FP.color.textMuted }}>
                Chasis {sel.chasis} · RD$ {fmtMoney(reciboDeFila(sel).valor)}
              </Typography>
              {filasDelRecibo(sel).length > 1 && (
                <Typography sx={{ fontSize: "0.8rem", color: FP.color.danger, mt: 0.6 }}>
                  Es un cobro combinado: se anulan sus {filasDelRecibo(sel).length} formas de pago (
                  {filasDelRecibo(sel).map((r) => r.banco).join(", ")}).
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={btnGhost}>Cancelar</Button>
          <Button disableElevation startIcon={<HighlightOffIcon />} onClick={confirmarCancelar} disabled={cancelando} sx={btnSolid(FP.color.danger, "#BE123C")}>
            {cancelando ? "Anulando…" : "Anular"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
